import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { api, resolveImg } from "@/lib/api";
import { STORY_CATEGORIES, IMAGES } from "@/lib/content";
import { BookOpen } from "lucide-react";

export default function Stories() {
  const [active, setActive] = useState("All");
  const { data: stories = [], isLoading } = useQuery({
    queryKey: ["stories"],
    queryFn: async () => (await api.get("/stories")).data,
  });

  const filtered = active === "All" ? stories : stories.filter((s) => s.category === active);

  return (
    <>
      <Seo title="Stories" path="/stories" description="Field notes and editorial stories from Planet Trotter Foundation: conservation, responsible travel, communities, agriculture, education and research, from India and around the world." />
      <PageHero
        eyebrow="Stories"
        titleLines={["Stories from", "the field."]}
        intro="An editorial record of the Foundation's work: the places, people and decisions behind each programme."
        image={IMAGES.river}
      />

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <div className="flex flex-wrap gap-2 mb-12">
            {["All", ...STORY_CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`story-filter-${c.replace(/\s/g, "").toLowerCase()}`}
                onClick={() => setActive(c)}
                className={`text-[0.7rem] uppercase tracking-[0.14em] px-4 py-2 border transition-colors duration-300 ${
                  active === c ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p className="text-ptf-text/50">Loading…</p>
          ) : filtered.length === 0 ? (
            <Reveal>
              <div data-testid="stories-empty" className="border border-dashed border-ptf-primary/25 py-24 px-8 text-center">
                <span className="grid place-items-center h-16 w-16 rounded-full bg-ptf-primary/5 mx-auto mb-6">
                  <BookOpen className="h-7 w-7 text-ptf-secondary" />
                </span>
                <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary">The first chapter is being written.</h3>
                <p className="mt-4 max-w-lg mx-auto text-ptf-text/70 leading-relaxed">
                  Stories from the field will appear here as our programmes develop.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((s, i) => (
                <Reveal key={s.id} delay={(i % 3) * 0.06}>
                  <Link to={`/stories/${s.id}`} data-testid={`story-card-${s.id}`} className="group h-full flex flex-col">
                    <div className="aspect-[16/10] overflow-hidden bg-ptf-primary/5">
                      {s.image && <img src={resolveImg(s.image)} alt={s.title} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />}
                    </div>
                    <div className="pt-5">
                      <span className="text-[0.6rem] uppercase tracking-[0.16em] text-ptf-secondary">{s.category}{s.date ? ` · ${s.date}` : ""}</span>
                      <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mt-2 leading-none group-hover:text-ptf-secondary transition-colors">{s.title}</h3>
                      {s.excerpt && <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed">{s.excerpt}</p>}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
